/* src/lessons/Scene.js */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { get, isEmpty, capitalize, map } from 'lodash';

import { withStyles } from '@material-ui/core/styles';
import { Paper, Checkbox, TextField, Grid, Typography } from '@material-ui/core';
import Chip from '@material-ui/core/Chip';
import { HorizontalBar } from 'react-chartjs-2';

import CustomSelect from '../../app/components/Select';
import { HeaderTable } from '../../app/components';
import LessonCard from './cards/LessonCard';
import { LineGraph, PieChart } from '../dashboard/components';

import { fetchLessons, fetchLesson, setLessonPage, setLessonPageCount } from './actions';
import { lessonSetInfo } from './cards/LessonCard/actions';
import { getLessons, getLessonInfo, getPageInfo } from './selectors';
import {
  changeNumberOfLessonsTimeFrame,
  changeNumberOfLessonsType,
  changeNumberOfLessonsStartDate,
  changeNumberOfLessonsEndDate,
} from '../dashboard/actions';
import { getNumLessons } from '../dashboard/selectors';
import { loadTexts } from '../../config/texts';

const TEXT_KEY = 'lessons';

class Lessons extends React.Component {
  constructor(props) {
    super(props);
    this.texts = loadTexts(TEXT_KEY, props.locale);
    this._timeFrames = [
      { value: 'day', label: this.texts.labels.day },
      { value: 'week', label: this.texts.labels.week },
      { value: 'month', label: this.texts.labels.month },
      { value: 'year', label: this.texts.labels.year },
    ];

    this._types = [
      { value: 'requested', label: this.texts.labels.requested },
      { value: 'completed', label: this.texts.labels.completed },
      { value: 'cancelled', label: this.texts.labels.cancelled },
      { value: 'no_show', label: this.texts.labels.no_show },
    ];

    this.state = {
      open: false,
      filterByArray: [],
      sort: [{ field: 'created_at', direction: -1 }],
      startDate: '',
      endDate: '',
    };
    this._onPopupClose = this._onPopupClose.bind(this);
    this._onSelectLesson = this._onSelectLesson.bind(this);
    this.changeFilterStatus = this.changeFilterStatus.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.keyBoardPressed = this.keyBoardPressed.bind(this);
    this.renderChips = this.renderChips.bind(this);
    this.renderFilter = this.renderFilter.bind(this);

    this._onNextPage = this._onNextPage.bind(this);
    this._onPrevPage = this._onPrevPage.bind(this);
    this._onSort = this._onSort.bind(this);
    this._onFetchLessons = this._onFetchLessons.bind(this);

    this._onTimeFrameChange = this._onTimeFrameChange.bind(this);
    this._onTypeChange = this._onTypeChange.bind(this);
    this._onChangeStartDate = this._onChangeStartDate.bind(this);
    this._onChangeEndDate = this._onChangeEndDate.bind(this);
    this._onClickUpdate = this._onClickUpdate.bind(this);
  }

  async componentDidMount() {
    this._onFetchLessons({});
    const { tasks } = this.props;
    if (isEmpty(tasks)) return;
    for (let task of tasks) {
      switch (task.action) {
        case 'fetch':
          await this.props.fetchLesson({ lesson: task.payload });
          await new Promise(resolve => {
            this.props.lessonSetInfo(this.props.info);
            this.setState({ open: true }, () => {
              resolve();
            });
          });
          break;
        default:
          break;
      }
    }
  }

  componentWillReceiveProps(nextProps) {
    if (
      nextProps.selectedUniversity !== this.props.selectedUniversity
      || nextProps.selectedOrganization !== this.props.selectedOrganization
    ) {
      this._onFetchLessons({
        university: nextProps.selectedUniversity,
        organization: nextProps.selectedOrganization,
      });
    }
  }

  handleDelete(chip) {
    const { filterByArray } = this.state;
    const chipData = [...filterByArray];
    const chipToDelete = chipData.indexOf(chip);

    chipData.splice(chipToDelete, 1);
    this.setState({ filterByArray: chipData });
  }

  keyBoardPressed(ev) {
    const { searchBy, searchByValue, filterByArray } = this.state;

    if (ev.key === 'Enter' && searchByValue !== '' && searchBy !== '') {
      const temp = filterByArray;
      temp.push({ searchBy, value: ev.target.value });
      this.setState({ searchByValue: '', filterByArray: temp });
    }
  }

  changeFilterStatus(status) {
    this.setState({ searchBy: status });
  }

  async _onSelectLesson({ _id }) {
    await this.props.fetchLesson({ lesson: _id });
    this.props.lessonSetInfo(this.props.info);
    this.setState({ open: true });
  }

  _onPopupClose() {
    this.setState({ open: false });
  }

  async _onFetchLessons({
    university = this.props.selectedUniversity,
    organization = this.props.selectedOrganization,
  }) {
    const { sort } = this.state;
    const { page, limit } = this.props.pagination;
    await this.props.fetchLessons({
      lessonSearchInput: { universities: [university], organizations: [organization] },
      sort,
      page: { page, limit },
    });
    this.props.setLessonPageCount(this.props.lessons.length);
  }

  _onNextPage() {
    this.props.setLessonPage(this.props.pagination.page + 1);
    setTimeout(() => this._onFetchLessons({}), 0);
  }

  _onPrevPage() {
    this.props.setLessonPage(this.props.pagination.page - 1);
    setTimeout(() => this._onFetchLessons({}), 0);
  }

  _onSort({ field, direction }) {
    let { sort } = this.state;
    switch (field) {
      case 'time':
        sort = [{ field: 'current.start_time', direction }];
        break;
      case 'price':
        sort = [{ field: 'price', direction }];
        break;
      case 'status':
        sort = [{ field: 'status', direction }];
        break;
      case 'tutor':
        sort = [{ field: 'tutor.user.first', direction }, { field: 'tutor.user.last', direction }];
        break;
      case 'student':
        sort = [{ field: 'student.user.first', direction }, { field: 'student.user.last', direction }];
        break;
      case 'course':
        sort = [{ field: 'course.subject', direction }, { field: 'course.number', direction }];
        break;
      case 'location':
        sort = [{ field: 'location.name', direction }];
        break;
      case 'created':
        sort = [{ field: 'created_at', direction }];
        break;
      default:
        break;
    }
    this.setState({ sort }, () => this._onFetchLessons({}));
  }

  renderChips() {
    const { classes } = this.props;
    const { filterByArray } = this.state;
    return filterByArray.map((c, index) => (
      <Chip
        key={index}
        label={`${c.searchBy}: ${c.value}`}
        onDelete={() => this.handleDelete(c)}
        className={classes.chip}
      />
    ));
  }

  renderFilter() {
    const { classes } = this.props;
    const { searchBy, searchByValue } = this.state;
    const dataArray = [
      'Course Code',
      'Course Name',
      'University',
      'Student',
      'Tutor',
      'Status',
      'Price',
      'Location',
    ];

    return (
      <Grid container justify="space-between" alignItems="flex-start" className={classes.gridContainer}>
        <div style={{ maxWidth: '80%' }}><TextField
          id="search"
          label={`Search ${searchBy}`}
          value={searchByValue}
          type="search"
          className={classes.textField}
          margin="normal"
          onChange={event => { this.setState({ searchByValue: event.target.value }); }}
          onKeyPress={ev => this.keyBoardPressed(ev)}
        />
          <div>{this.renderChips()}</div>
        </div>
        <CustomSelect dataArray={dataArray} onValueChange={this.changeFilterStatus} withDefault />
      </Grid>
    );
  }

  _onTimeFrameChange(value) {
    this.props.changeNumberOfLessonsTimeFrame(value);
  }

  _onTypeChange(value) {
    this.props.changeNumberOfLessonsType(value);
  }

  _onChangeStartDate(value) {
    this.setState({ startDate: value });
  }

  _onChangeEndDate(value) {
    this.setState({ endDate: value });
  }

  _onClickUpdate() {
    const { startDate, endDate } = this.state;
    this.props.changeNumberOfLessonsStartDate(startDate);
    this.props.changeNumberOfLessonsEndDate(endDate);
  }

  renderGeneralLessonStats() {
    const { classes } = this.props;
    const temp = [
      { field: 'Whiteboard Used', value: '44' },
      { field: 'Average Lesson Duration', value: '1.5' },
      { field: 'Total Hours Taught', value: '3041' }
    ];
    return ;
    return (
      <div className={classes.lessonStatContainer}>
        {map(temp, d => (
          <div className={classes.lessonData}>
            <Typography variant="display2">
              {d.value.split(' ')[0]}
            </Typography>
            <Typography variant="subheading" style={{ marginTop: 10 }}>
              {d.field}
            </Typography>
          </div>
        ))}
      </div>
    )
  }

  renderOnlineLessonStats() {
    const options = { scales: { xAxes: [{ stacked: true }], yAxes: [{ stacked: true }] } }
    const { labels } = this.texts;
    const graphData = {
      labels: ['Online 1-1 sessions'],
      datasets: [
      {
        label: labels.web,
        backgroundColor: 'rgba(84,84,178,0.4)',
        borderColor: 'rgb(84,84,178)',
        borderWidth: 1,
        hoverBackgroundColor: 'rgba(84,84,178,0.4)',
        hoverBorderColor: 'rgb(84,84,178)',
        data: [20, 54],
      },
      {
        label: labels.in_app,
        backgroundColor: 'rgba(64,8,178,0.4)',
        borderColor: 'rgb(64,8,178)',
        borderWidth: 1,
        hoverBackgroundColor: 'rgba(64,8,178,0.4)',
        hoverBorderColor: 'rgb(64,8,178)',
        data: [10, 44],
      }
    ]
    }
    return (
      <Paper>
        <HorizontalBar data={graphData} options={options} />
      </Paper>
    )
  }

  renderAnalytics() {
    const { currentTimeFrame, currentType, lessonsData: { data, labels } } = this.props;
    const { startDate, endDate } = this.state;

    return (
      <Grid container spacing={16} alignItems="flex-end" justify="space-between">
        <Grid item xs={12} sm={7}>
          <Grid container spacing={16} direction="column">
            <Grid item xs={12} sm={12}>
              {this.renderGeneralLessonStats()}
            </Grid>
            <Grid item xs={12} sm={12}>
              <Paper>
                <LineGraph
                  title={this.texts.title.analytics}
                  data={data}
                  labels={labels}
                  label={capitalize(currentType)}
                  onTimeFrameChange={this._onTimeFrameChange}
                  onTypeChange={this._onTypeChange}
                  timeFrames={this._timeFrames}
                  types={this._types}
                  currentTimeFrame={currentTimeFrame}
                  currentType={currentType}
                  startDate={startDate}
                  endDate={endDate}
                  onChangeStartDate={this._onChangeStartDate}
                  onChangeEndDate={this._onChangeEndDate}
                  onClickUpdate={this._onClickUpdate}
                />
              </Paper>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={5}>
          <Grid container spacing={16} direction="column">
            <Grid item xs={12} sm={12}>
              <Paper>
                {this.renderOnlineLessonStats()}
              </Paper>
            </Grid>
            {/*<Grid item xs={12} sm={12}>
              <Paper>
                <PieChart title="Device Used" labels={['Imac', 'PC', 'PC', 'PC']} data={[100, 200, 200, 200]} height={300} />
              </Paper>
            </Grid>*/}
          </Grid>
        </Grid>
      </Grid>
    )
  }

  render() {
    const {
      classes,
      lessons,
      count,
      pagination: { start, end },
    } = this.props;
    const { open } = this.state;
    return (
      <Grid container spacing={16} className={classes.container}>
        <Grid item xs={12}>
          {this.renderAnalytics()}
        </Grid>
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            {/* this.renderFilter() */}
            <Typography variant="display1" color="primary" style={{ marginBottom: 20 }}>
              {this.texts.title.lesson}
            </Typography>
            <HeaderTable
              label="lessons"
              data={lessons}
              hover
              onRowClick={this._onSelectLesson}
              paging
              start={start}
              end={end}
              count={count}
              onNext={this._onNextPage}
              onPrev={this._onPrevPage}
              onSort={this._onSort}
              sortEnabled
            />
            <LessonCard open={open} onClose={this._onPopupClose} />
          </Paper>
        </Grid>
      </Grid>
    );
  }
}

const styles = theme => ({
  lessonStatContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  lessonData: {
    marginRight: 40,
    alignItems: 'center',
    marginTop: 20,
    marginBottom: 40,
    display: 'flex',
    flexDirection: 'column',
  },
  container: {
    padding: theme.spacing.unit * 3
  },
  paper: {
    width: '100%',
    overflowX: 'auto',
    padding: theme.spacing.unit * 3
  },
  gridContainer: {
    padding: theme.spacing.unit,
  },
  chip: {
    margin: theme.spacing.unit,
  },
});

Lessons.propTypes = {
  classes: PropTypes.object.isRequired,
  selectedUniversity: PropTypes.string.isRequired,
  selectedOrganization: PropTypes.string.isRequired,
  fetchLessons: PropTypes.func.isRequired,
  fetchLesson: PropTypes.func.isRequired,
  lessonSetInfo: PropTypes.func.isRequired,
  lessons: PropTypes.array.isRequired,
  info: PropTypes.object.isRequired,
  pagination: PropTypes.object.isRequired,
  setLessonPageCount: PropTypes.func.isRequired,
  setLessonPage: PropTypes.func.isRequired,
  count: PropTypes.number.isRequired,
};

const mapStateToProps = state => ({
  lessons: getLessons(state),
  selectedUniversity: state.auth.selectedUniversity,
  selectedOrganization: state.auth.selectedOrganization,
  organizations: get(state, 'auth.data.user.organizations'),
  info: getLessonInfo(state),
  // fetchState: fetchCoursesState(state),
  pagination: getPageInfo(state),
  count: state.lessons.main.data.count || 0,
  tasks: get(state, 'router.location.state.tasks') || [],
  currentTimeFrame: state.dashboard.graphs.numberOfLessonsTimeFrame,
  currentType: state.dashboard.graphs.numberOfLessonsType,
  lessonsData: getNumLessons(state),
  locale: state.appState.locale
});

const mapDispatchToProps = dispatch => ({
  fetchLessons: params => dispatch(fetchLessons(params)()),
  fetchLesson: params => dispatch(fetchLesson(params)()),
  lessonSetInfo: params => dispatch(lessonSetInfo(params)),
  setLessonPage: params => dispatch(setLessonPage(params)),
  setLessonPageCount: params => dispatch(setLessonPageCount(params)),
  changeNumberOfLessonsTimeFrame: params => dispatch(changeNumberOfLessonsTimeFrame(params)),
  changeNumberOfLessonsType: params => dispatch(changeNumberOfLessonsType(params)),
  changeNumberOfLessonsStartDate: params => dispatch(changeNumberOfLessonsStartDate(params)),
  changeNumberOfLessonsEndDate: params => dispatch(changeNumberOfLessonsEndDate(params)),
});

export default compose(
  withStyles(styles, { name: 'Lessons' }),
  connect(mapStateToProps, mapDispatchToProps)
)(Lessons);



// WEBPACK FOOTER //
// ./src/modules/lessons/Scene.js